import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Coins,
  Car,
  Users,
  CheckCircle,
  Calculator,
} from "lucide-react";
import ResponsiveAppBar from "../components/AppBar";
import Footer from "../components/Footer";

function LoanSelectionPage() {
  const navigate = useNavigate();

  const loanProducts = [
    {
      title: "Gold Loan (Jewel)",
      tag: "Fastest Approval",
      desc: "Avail instant cash against gold ornaments with the highest market per-gram valuation and complete safety.",
      features: [
        "Up to 75% - 80% of gold market value",
        "Interest rate starting from 1.2% per month",
        "Disbursal within 20 - 30 minutes",
        "Insured bank-grade locker storage",
      ],
      icon: Coins,
      link: "/gold-loan",
      badgeColor: "bg-[#936097]/15 text-[#936097] border-[#936097]/30",
      gradient: "from-[#936097] to-[#77c8c7]",
    },
    {
      title: "Vehicle Loan",
      tag: "Up to 90% Financing",
      desc: "Finance new or pre-owned two-wheelers, cars, and commercial vehicles with customized EMI options.",
      features: [
        "Up to 90% on-road funding",
        "Competitive rates from 12.8% p.a.",
        "Flexible tenures from 12 to 60 months",
        "Quick digital document verification",
      ],
      icon: Car,
      link: "/vehicle-loan",
      badgeColor: "bg-[#77c8c7]/15 text-[#0f766e] border-[#77c8c7]/30",
      gradient: "from-[#77c8c7] to-[#936097]",
    },
    {
      title: "Chit & Savings Fund",
      tag: "Group Scheme",
      desc: "Smart savings coupled with emergency bidding facilities. Transparent accounting with regular monthly dividends.",
      features: [
        "Regulated monthly auction plans",
        "High dividend yields on savings",
        "Emergency credit access anytime",
        "Transparent online passbook tracking",
      ],
      icon: Users,
      link: "/contact",
      buttonText: "Contact Us",
      badgeColor: "bg-[#936097]/15 text-[#936097] border-[#936097]/30",
      gradient: "from-[#936097] to-[#77c8c7]",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 flex flex-col justify-between">
      <div>
        <ResponsiveAppBar />

        {/* Hero Header */}
        <section className="bg-gradient-to-b from-white via-[#f8fafc] to-[#eff6ff] text-slate-900 py-16 px-6 text-center relative overflow-hidden border-b border-slate-200/60">
          <div className="absolute top-0 left-1/3 w-80 h-80 bg-[#936097]/10 rounded-full filter blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-[#77c8c7]/15 rounded-full filter blur-[120px] pointer-events-none" />

          <div className="container mx-auto max-w-3xl relative z-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-teal-50 text-[#0d9488] text-xs font-bold uppercase tracking-wider mb-3 border border-teal-200/60">
              Transparent Financing
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4 text-slate-900">
              Select Your Ideal Loan Solution
            </h1>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Explore our transparent loan products tailored for quick approval, low interest, and maximum financial flexibility.
            </p>
          </div>
        </section>

        {/* Loan Cards Grid */}
        <section className="container mx-auto px-6 py-16 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            {loanProducts.map((product, idx) => (
              <div
                key={idx}
                className="bg-white rounded-3xl p-8 border border-slate-200/90 shadow-lg hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#936097]/15 to-[#77c8c7]/15 flex items-center justify-center text-[#936097]">
                      <product.icon className="w-7 h-7" />
                    </div>
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full border ${product.badgeColor}`}
                    >
                      {product.tag}
                    </span>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {product.title}
                  </h2>
                  <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                    {product.desc}
                  </p>

                  <div className="space-y-3 mb-8">
                    {product.features.map((feat, fIdx) => (
                      <div key={fIdx} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-[#77c8c7] shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to={product.link}
                  className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-[#936097] to-[#77c8c7] text-white font-bold text-sm hover:opacity-95 shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <span>{product.buttonText || "Apply Now"}</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          {/* Quick Help & Calculator Box */}
          <div className="mt-14 bg-gradient-to-r from-[#936097] via-[#855389] to-[#77c8c7] rounded-3xl p-8 sm:p-10 text-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
            <div className="space-y-2 text-center sm:text-left">
              <h3 className="text-2xl font-bold flex items-center justify-center sm:justify-start gap-2 text-white">
                <Calculator className="w-6 h-6 text-white" />
                Not Sure About Monthly Payments?
              </h3>
              <p className="text-white/90 text-sm">
                Use our interactive EMI calculator to plan your budget with ease before applying.
              </p>
            </div>

            <button
              onClick={() => navigate("/emi")}
              className="px-6 py-3.5 rounded-xl bg-white text-[#936097] hover:bg-slate-50 font-bold text-sm transition-all shadow-md cursor-pointer shrink-0"
            >
              Open EMI Calculator →
            </button>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}

export default LoanSelectionPage;
